import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowUpRight, Calendar } from "lucide-react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import GuestbookForm from "../components/site/GuestbookForm";
import { fetchGuestbook, fetchGuestbookStats, mediaUrl } from "../lib/api";
import useLenis from "../hooks/useLenis";

const FILTERS = [
    { value: "all", label: "Alle" },
    { value: "thomas", label: "Thomas" },
    { value: "bernhard", label: "Bernhard" },
    { value: "general", label: "Allgemein" },
];

const CAT_LABEL = {
    thomas: "Thomas Rabitsch",
    bernhard: "Bernhard Rabitsch",
    general: "Allgemein",
};

const CAT_ACCENT = {
    thomas: "var(--rb-primary)",
    bernhard: "var(--rb-secondary)",
    general: "#a1a1aa",
};

const CHAPTERS = [
    {
        n: "01",
        title: "Ein Konzert. Ein Moment.",
        body: "Vom U4 bis zum Donauinselfest — jeder Auftritt trug eine eigene Nacht mit sich. Manche Erinnerungen halten länger als ganze Alben.",
    },
    {
        n: "02",
        title: "Ein Song, der bleibt.",
        body: "Die Melodie, die dich zurückwirft. Der Refrain, den du noch immer mitsingst. Erzähl uns, welche Zeile du nie mehr loslässt.",
    },
    {
        n: "03",
        title: "Werde Teil des Archivs.",
        body: "Jede Zuschrift wird persönlich gelesen und moderiert. Danach wird sie Teil einer wachsenden Sammlung aus Fanmomenten, Tour-Erinnerungen und leisen Geschichten.",
    },
];

// Line-by-line masked reveal for the hero
const heroWords = ["Das", "Gästebuch"];

export default function GuestbookPage() {
    useLenis(true);
    const [params, setParams] = useSearchParams();
    const activeCat = params.get("category") || "all";
    const [entries, setEntries] = useState([]);
    const [stats, setStats] = useState({ total: 0, thomas: 0, bernhard: 0, general: 0 });
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const [defaultFormCat, setDefaultFormCat] = useState("general");

    const load = () => {
        setLoading(true);
        Promise.all([fetchGuestbook(activeCat === "all" ? null : activeCat), fetchGuestbookStats()])
            .then(([es, st]) => {
                setEntries(es);
                setStats(st);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, [activeCat]);

    const setFilter = (v) => {
        if (v === "all") setParams({});
        else setParams({ category: v });
    };

    const openForm = (cat = "general") => {
        setDefaultFormCat(cat);
        setFormOpen(true);
    };

    const countForFilter = (v) => {
        if (v === "all") return stats.total;
        return stats[v] ?? 0;
    };

    return (
        <div className="relative min-h-screen bg-[var(--rb-bg)] text-white" data-testid="guestbook-page">
            <Navbar />

            {/* HERO */}
            <section className="relative min-h-[92vh] flex items-end pb-20 md:pb-28 px-6 md:px-10 overflow-hidden">
                {/* Grid backdrop */}
                <div
                    aria-hidden
                    className="absolute inset-0 opacity-[0.07]"
                    style={{
                        backgroundImage:
                            "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
                        backgroundSize: "88px 88px",
                        maskImage: "radial-gradient(circle at 30% 40%, black, transparent 75%)",
                        WebkitMaskImage: "radial-gradient(circle at 30% 40%, black, transparent 75%)",
                    }}
                />
                <ParallaxBlob />

                <div className="relative z-10 max-w-[1400px] mx-auto w-full">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8 }}
                        className="flex items-center gap-3 text-xs uppercase tracking-[0.32em] text-[var(--rb-primary)] mb-6"
                    >
                        <span className="block w-8 h-px bg-[var(--rb-primary)]" />
                        <span>Erinnerungsarchiv</span>
                    </motion.div>

                    <h1 className="font-display text-6xl md:text-8xl lg:text-[10rem] leading-[0.92] text-white">
                        {heroWords.map((w, i) => (
                            <span key={i} className="block overflow-hidden">
                                <motion.span
                                    initial={{ y: "110%" }}
                                    animate={{ y: "0%" }}
                                    transition={{
                                        duration: 1.2,
                                        delay: 0.15 + i * 0.14,
                                        ease: [0.22, 1, 0.36, 1],
                                    }}
                                    className="inline-block will-change-transform"
                                >
                                    {w === "Gästebuch" ? (
                                        <em className="not-italic text-[var(--rb-primary)] font-normal italic">
                                            {w}
                                        </em>
                                    ) : (
                                        w
                                    )}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <div className="mt-10 grid md:grid-cols-[1fr_auto] gap-8 items-end">
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.6 }}
                            className="max-w-2xl text-lg md:text-xl text-zinc-300/90 leading-relaxed"
                        >
                            <em className="text-[var(--rb-secondary)] not-italic italic">
                                Eine Geschichte. Ein Moment. Eine Erinnerung.
                            </em>
                            <br />
                            Du hast Thomas oder Bernhard einmal live erlebt? Eine besondere
                            Erinnerung an Falco, Drahdiwaberl oder eines ihrer anderen musikalischen
                            Projekte? Teile deine Geschichte mit anderen Fans und werde Teil des
                            rabitsch.supporter Erinnerungsarchivs.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.75 }}
                            className="flex flex-col items-start md:items-end gap-3"
                        >
                            <div className="font-display text-6xl md:text-7xl leading-none text-white">
                                {stats.total}
                            </div>
                            <div className="text-xs uppercase tracking-[0.32em] text-zinc-500">
                                {stats.total === 1 ? "Eintrag" : "Einträge"} · veröffentlicht
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Marquee */}
            <MarqueeBar />

            {/* Chapters manifesto */}
            <section className="relative px-6 md:px-10 py-24 md:py-32">
                <div className="max-w-[1400px] mx-auto grid md:grid-cols-3 gap-8 md:gap-12">
                    {CHAPTERS.map((c, i) => (
                        <motion.div
                            key={c.title}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.7, delay: i * 0.1 }}
                            className="relative"
                        >
                            <div className="w-10 h-px bg-[var(--rb-primary)] mb-6" />
                            <h3 className="font-display text-2xl md:text-3xl text-white mb-3 leading-tight">
                                {c.title}
                            </h3>
                            <p className="text-zinc-400 leading-relaxed">{c.body}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Filter + CTA sticky bar */}
            <section
                id="entries"
                className="sticky top-16 z-30 bg-[var(--rb-bg)]/85 backdrop-blur-xl border-y border-white/5"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex flex-wrap gap-3 items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap" data-testid="guestbook-filters">
                        {FILTERS.map((f) => (
                            <button
                                key={f.value}
                                onClick={() => setFilter(f.value)}
                                data-testid={`filter-${f.value}`}
                                className={`group px-4 py-2 text-xs uppercase tracking-[0.24em] border transition-all ${
                                    activeCat === f.value
                                        ? "border-[var(--rb-primary)] text-white bg-[var(--rb-primary)]/10"
                                        : "border-white/10 text-zinc-400 hover:text-white"
                                }`}
                            >
                                {f.label}
                                <span className="ml-2 text-[10px] text-zinc-500 group-hover:text-[var(--rb-secondary)]">
                                    {countForFilter(f.value)}
                                </span>
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => openForm(activeCat === "all" ? "general" : activeCat)}
                        data-testid="guestbook-new-btn"
                        className="flex items-center gap-2 bg-[var(--rb-primary)] hover:bg-[var(--rb-primary-hover)] text-black px-5 py-3 text-xs uppercase tracking-[0.28em] transition-all hover:-translate-y-0.5"
                    >
                        <Plus className="w-4 h-4" strokeWidth={2.4} />
                        Eintrag hinterlassen
                    </button>
                </div>
            </section>

            {/* Entries */}
            <section className="relative px-6 md:px-10 py-16 md:py-24">
                <div className="max-w-[1400px] mx-auto">
                    {loading ? (
                        <div className="text-zinc-500 uppercase tracking-[0.32em] text-xs">Lade …</div>
                    ) : entries.length === 0 ? (
                        <EmptyState onOpen={() => openForm(activeCat === "all" ? "general" : activeCat)} />
                    ) : (
                        <div className="columns-1 md:columns-2 lg:columns-3 gap-6" data-testid="guestbook-list">
                            <AnimatePresence mode="popLayout">
                                {entries.map((e, i) => (
                                    <EntryCard key={e.id} entry={e} index={i} />
                                ))}
                            </AnimatePresence>
                        </div>
                    )}
                </div>
            </section>

            <Footer />

            <GuestbookForm
                open={formOpen}
                defaultCategory={defaultFormCat}
                onClose={() => setFormOpen(false)}
                onSubmitted={load}
            />
        </div>
    );
}

const EntryCard = ({ entry, index }) => {
    const accent = CAT_ACCENT[entry.category] || "var(--rb-primary)";
    return (
        <motion.article
            layout
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.6, delay: Math.min(index * 0.05, 0.5) }}
            className="mb-6 break-inside-avoid group"
            data-testid={`entry-${entry.id}`}
        >
            <div className="relative bg-[#111]/80 border border-white/5 hover:border-[var(--rb-primary)]/50 p-6 md:p-7 transition-all">
                {entry.photo_url && (
                    <div className="relative -mx-6 md:-mx-7 -mt-6 md:-mt-7 mb-6 overflow-hidden aspect-[4/3] bg-black">
                        <img
                            src={mediaUrl(entry.photo_url)}
                            alt={entry.name}
                            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    </div>
                )}
                <div
                    className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] mb-4"
                    style={{ color: accent }}
                >
                    <span className="block w-4 h-px" style={{ background: accent }} />
                    {CAT_LABEL[entry.category] || "Allgemein"}
                </div>
                <blockquote className="font-display text-xl md:text-[22px] text-white leading-snug italic">
                    “{entry.message}”
                </blockquote>
                <div className="mt-6 flex items-center gap-4 text-xs uppercase tracking-[0.24em] text-zinc-500">
                    <span className="text-zinc-300">— {entry.name}</span>
                    {entry.year && (
                        <span className="flex items-center gap-1.5">
                            <Calendar className="w-3 h-3" />
                            {entry.year}
                        </span>
                    )}
                    <span className="ml-auto text-[10px] text-zinc-600">
                        {new Date(entry.published_at || entry.created_at).toLocaleDateString("de-AT", {
                            year: "numeric",
                            month: "short",
                            day: "2-digit",
                        })}
                    </span>
                </div>
            </div>
        </motion.article>
    );
};

const EmptyState = ({ onOpen }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center py-16 md:py-24"
        data-testid="guestbook-empty"
    >
        <div className="font-display text-4xl md:text-5xl text-white mb-4">
            Noch keine Einträge.
        </div>
        <p className="text-zinc-400 max-w-md mx-auto mb-8">
            Sei der erste, der eine Geschichte erzählt. Ein Konzert, ein Moment, eine Note, die
            geblieben ist.
        </p>
        <button
            onClick={onOpen}
            className="inline-flex items-center gap-2 bg-[var(--rb-primary)] hover:bg-[var(--rb-primary-hover)] text-black px-6 py-3 text-xs uppercase tracking-[0.28em] transition-all hover:-translate-y-0.5"
        >
            <Plus className="w-4 h-4" strokeWidth={2.4} />
            Ersten Eintrag hinterlassen
        </button>
    </motion.div>
);

/** Slow editorial marquee — Falco/Drahdiwaberl/Untouchables/Amadeus… */
const MarqueeBar = () => {
    const words = [
        "Donauinsel 1993",
        "•",
        "U4 Goldfisch' Nächte",
        "•",
        "MiniMoog",
        "•",
        "Amadeus Award",
        "•",
        "Falco",
        "•",
        "Drahdiwaberl",
        "•",
        "The Untouchables",
        "•",
        "Eurovision '83",
        "•",
        "Austropop",
    ];
    return (
        <div className="relative border-y border-white/5 py-5 overflow-hidden">
            <motion.div
                animate={{ x: ["0%", "-50%"] }}
                transition={{ duration: 60, ease: "linear", repeat: Infinity }}
                className="flex gap-10 whitespace-nowrap font-display text-3xl md:text-5xl text-white/70"
            >
                {[...words, ...words].map((w, i) => (
                    <span key={i} className={w === "•" ? "text-[var(--rb-primary)]" : ""}>
                        {w}
                    </span>
                ))}
            </motion.div>
        </div>
    );
};

const ParallaxBlob = () => (
    <>
        <motion.div
            aria-hidden
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2, delay: 0.4 }}
            className="pointer-events-none absolute -top-24 -right-24 w-[560px] h-[560px] rounded-full"
            style={{
                background:
                    "radial-gradient(circle, rgba(232,113,50,0.28) 0%, rgba(232,113,50,0) 65%)",
                filter: "blur(8px)",
            }}
        />
        <motion.div
            aria-hidden
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2, delay: 0.6 }}
            className="pointer-events-none absolute bottom-0 -left-32 w-[520px] h-[520px] rounded-full"
            style={{
                background:
                    "radial-gradient(circle, rgba(229,183,94,0.18) 0%, rgba(229,183,94,0) 65%)",
                filter: "blur(10px)",
            }}
        />
    </>
);
